import { motion } from "framer-motion";
import { Glasses, Search, Heart, Eye } from "lucide-react";

/**
 * Scene 55 — Why video matters · stage-prop driven.
 *
 *   stage = 0  →  Full-bleed AR-glasses image fades in alone (pure cinema).
 *   stage = 1  →  Eyebrow + headline + supporting line + verbs layer in
 *                 over a darker tint of the same image.
 */

const IMAGE_SRC = "/property-ai-future.png";

const verbs = [
  { icon: Search, label: "Discovery" },
  { icon: Heart, label: "Trust" },
  { icon: Eye, label: "Decision" },
];

export default function Scene55_VideoCritical({ stage = 0 }) {
  return (
    <section
      className="absolute inset-0 overflow-hidden"
      data-testid="scene-video-critical"
    >
      {/* Full bleed image — fades in on stage 0, stays underneath on stage 1 */}
      <motion.img
        src={IMAGE_SRC}
        alt="Person wearing AR glasses viewing property listings on a London street"
        className="absolute inset-0 w-full h-full object-cover"
        initial={{ scale: 1.06, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
      />

      {/* Cinematic gradient overlay — darkens further on stage 1 for legibility */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background:
            stage === 0
              ? "linear-gradient(180deg, rgba(7,18,12,0.35) 0%, rgba(7,18,12,0.0) 30%, rgba(7,18,12,0.0) 70%, rgba(7,18,12,0.55) 100%)"
              : "linear-gradient(90deg, rgba(7,18,12,0.94) 0%, rgba(7,18,12,0.55) 35%, rgba(7,18,12,0.30) 60%, rgba(7,18,12,0.55) 90%, rgba(7,18,12,0.92) 100%)",
        }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      />
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background:
            stage === 0
              ? "linear-gradient(180deg, rgba(7,18,12,0.30) 0%, rgba(7,18,12,0.0) 25%, rgba(7,18,12,0.0) 70%, rgba(7,18,12,0.65) 100%)"
              : "linear-gradient(180deg, rgba(7,18,12,0.55) 0%, rgba(7,18,12,0.0) 25%, rgba(7,18,12,0.0) 70%, rgba(7,18,12,0.85) 100%)",
        }}
        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
      />
      {/* Subtle grain */}
      <div className="absolute inset-0 grain pointer-events-none opacity-40" />

      {/* Stage 1 — content layers in */}
      {stage >= 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          className="relative max-w-[1500px] mx-auto w-full px-8 lg:px-16 h-full flex flex-col justify-center z-10"
        >
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="kn-eyebrow mb-8 flex items-center gap-3"
            >
              <Glasses className="w-3.5 h-3.5" style={{ color: "#e9a820" }} />
              Why video
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="kn-display text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.1]"
              style={{ textShadow: "0 4px 60px rgba(0,0,0,0.85)" }}
              data-testid="video-critical-headline"
            >
              Video is the{" "}
              <span style={{ color: "#e9a820", fontWeight: 800 }}>
                closest digital format
              </span>
              <br />
              to{" "}
              <span
                style={{
                  background:
                    "linear-gradient(135deg, #d4940a 0%, #e9a820 50%, #d4940a 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  fontWeight: 900,
                }}
              >
                real-world experience.
              </span>
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.4 }}
              className="mt-8 max-w-3xl text-lg sm:text-xl"
              style={{
                color: "rgba(255,255,255,0.9)",
                textShadow: "0 2px 30px rgba(0,0,0,0.85)",
              }}
            >
              As interfaces evolve{" "}
              <span style={{ color: "#e9a820", fontWeight: 700 }}>
                beyond phones
              </span>
              , video is what powers…
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 2.0 }}
              className="mt-8 flex flex-wrap items-center gap-3 sm:gap-4"
            >
              {verbs.map((v, i) => {
                const Icon = v.icon;
                return (
                  <motion.div
                    key={v.label}
                    initial={{ opacity: 0, y: 16, scale: 0.92 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                      delay: 2.1 + i * 0.18,
                      duration: 0.7,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    className="flex items-center gap-3 rounded-full glass-dark px-6 py-3"
                    data-testid={`video-verb-${i}`}
                  >
                    <Icon className="w-5 h-5" style={{ color: "#e9a820" }} />
                    <span
                      className="kn-display text-base sm:text-lg"
                      style={{ color: "#e2e8f0", fontWeight: 700 }}
                    >
                      {v.label}
                    </span>
                  </motion.div>
                );
              })}
            </motion.div>
        </motion.div>
      )}
    </section>
  );
}
